/**
 * The rail's fixed slot map.
 *
 * Order is declared here on purpose. Clinic setup comes before provider intake
 * because clinic answers seed the provider forms; deriving the rail from the
 * generated catalog would render those sections in the wrong order.
 */

export interface RailItem {
  pageKey: string;
  label: string;
}

export interface RailSection {
  key: string;
  label: string;
  phase: string;
  items: RailItem[];
}

export const RAIL_SECTIONS: RailSection[] = [
  {
    key: 'key_questions',
    label: 'Key questions',
    phase: 'Key questions',
    items: [
      { pageKey: 'K1', label: 'Key questions' },
      { pageKey: 'F1', label: "What we'll need" },
    ],
  },
  {
    key: 'clinic_setup',
    label: 'Clinic setup',
    phase: 'Clinic setup',
    items: [
      { pageKey: 'M1', label: 'Clinic documents' },
      { pageKey: 'M2', label: 'Practice identity' },
      { pageKey: 'M3', label: 'Addresses & contacts' },
      { pageKey: 'M5', label: 'Provider roster' },
      { pageKey: 'M6', label: 'Review' },
    ],
  },
  {
    key: 'your_intake',
    label: 'Your intake',
    phase: 'Your intake',
    items: [
      { pageKey: 'P1', label: 'Your documents' },
      { pageKey: 'P2', label: 'Your identifiers' },
      { pageKey: 'P3', label: 'Licenses & states' },
      { pageKey: 'P4', label: 'Malpractice' },
      { pageKey: 'P5', label: 'About you' },
      { pageKey: 'P6', label: 'Review' },
    ],
  },
  {
    key: 'finish',
    label: 'Finish',
    phase: 'Finish',
    items: [{ pageKey: 'X1', label: 'Confirm completion' }],
  },
  // The post-critical phase sits after Finish on purpose: its canvas (T1) is
  // never required to reach "done", so it must not appear between the review
  // pages and the completion page. It renders only when the generated form
  // carries an active post_critical canvas — otherwise `notNeededKeys` drops
  // T1 and IntakeShell hides the empty section and its phase.
  {
    key: 'post_critical',
    label: 'Later',
    phase: 'Later',
    items: [{ pageKey: 'T1', label: "Items we'll need later" }],
  },
];

export const PHASES = ['Key questions', 'Clinic setup', 'Your intake', 'Finish', 'Later'];

/** Canvas `section` value carrying the trailing, never-blocking items. */
export const TRAILING_SECTION = 'post_critical';

/** Rail page keys that render the trailing section, in rail order. */
export const TRAILING_PAGE_KEYS = (
  RAIL_SECTIONS.find(section => section.key === TRAILING_SECTION)?.items ?? []
).map(item => item.pageKey);

/** What a document is called on the forecast, keyed by the dictionary's source_doc. */
export const DOCUMENT_HINTS: Record<string, string> = {
  M1: 'W-9',
  M2: 'EIN, NPI Type 2, profile',
  M3: 'practice / billing / mailing',
  M5: 'providers who complete their own intake',
  P1: 'COI, licenses, ID, DEA, diploma, CV',
  P2: 'CAQH, NPI, taxonomy, DEA',
  P3: 'state licenses',
  P4: 'malpractice certificate',
  P5: 'identity details, references',
};
